import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps, TextFieldVariants } from "@mui/material/TextField";
import Name, { InvalidNameError } from "_shared/names/name";
import { useState } from "react";

interface NameInputContainerProps {
  direction?: "row" | "column";
}

const NameInputContainer = styled(Stack)<NameInputContainerProps>(({ theme, direction = "row" }) => ({
  flexDirection: direction,
  gap: direction === "row" ? theme.spacing(1.5) : 0,
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: 0,
  },
}));
const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
    width: "100%",
  },
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1.5),
}));

interface NameInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  nameValue?: Name;
  onChange?: (name?: Name, error?: string) => void;
  direction?: "row" | "column";
}

export default function NameInput(props: Readonly<NameInputProps>) {
  const { idPrefix, variant, nameValue, onChange, direction, ...otherProps } = props;

  const [firstNameValueText, setFirstNameValueText] = useState<string>(nameValue?.firstName ?? "");
  const [lastNameValueText, setLastNameValueText] = useState<string>(nameValue?.lastName ?? "");
  const [firstNameValueError, setFirstNameValueError] = useState<string>("");
  const [lastNameValueError, setLastNameValueError] = useState<string>("");

  function handleNameChanged(firstName?: string, lastName?: string) {
    let newName: Name | undefined;

    try {
      newName = buildName(firstName, lastName);
      setFirstNameValueError("");
      setLastNameValueError("");
      onChange?.(newName, undefined);
    } catch (error) {
      if (!(error instanceof InvalidNameError)) throw error;
      setFirstNameValueError(error.firstNameErrorMessage);
      setLastNameValueError(error.lastNameErrorMessage);
      onChange?.(newName, error.message);
    }
  }

  return (
    <NameInputContainer direction={direction}>
      <InputField
        id={idPrefix ? idPrefix + "-first-name-input" : "first-name-input"}
        label="First Name"
        onChange={(event) => {
          setFirstNameValueText(event.target.value);
          handleNameChanged(event.target.value, lastNameValueText);
        }}
        required
        value={firstNameValueText}
        error={!!firstNameValueError}
        helperText={firstNameValueError}
        variant={variant}
        autoComplete="given-name"
        {...otherProps}
      />
      <InputField
        id={idPrefix ? idPrefix + "-last-name-input" : "last-name-input"}
        label="Last Name"
        onChange={(event) => {
          setLastNameValueText(event.target.value);
          handleNameChanged(firstNameValueText, event.target.value);
        }}
        required
        value={lastNameValueText}
        error={!!lastNameValueError}
        helperText={lastNameValueError}
        variant={variant}
        autoComplete="family-name"
        {...otherProps}
      />
    </NameInputContainer>
  );
}

function buildName(firstName?: string, lastName?: string): Name | undefined {
  try {
    return Name.create({ params: { firstName, lastName }, options: { silenceSchemaErrors: true } }) ?? undefined;
  } catch (error) {
    if (error instanceof InvalidNameError) {
      throw error;
    }
  }
}
